import { supabase } from './supabase';

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  subscriberCount?: number;
}

export interface ValidationError {
  type: 'sendgrid' | 'business_info' | 'subscribers' | 'campaign_content';
  message: string;
  action?: string;
  actionLink?: string;
}

// filterTags: doelgroep-groepen (email_contacts.tags, OR-match); leeg = alle contacten.
export async function validateCampaignScheduling(userId: string, campaignId?: string, filterTags?: string[]): Promise<ValidationResult> {
  const errors: ValidationError[] = [];

  if (campaignId) {
    const { data: campaign, error: campaignError } = await supabase
      .from('email_campaigns')
      .select('subject, content')
      .eq('id', campaignId)
      .single();

    if (campaignError || !campaign) {
      errors.push({
        type: 'campaign_content',
        message: 'Kon campaign niet laden',
      });
      return { isValid: false, errors };
    }

    if (!campaign.subject || !campaign.subject.trim()) {
      errors.push({
        type: 'campaign_content',
        message: 'Subject is verplicht',
        action: 'Vul een subject in voor je campaign',
        actionLink: `/campaigns/edit/${campaignId}`,
      });
    }

    if (!campaign.content || !campaign.content.trim()) {
      errors.push({
        type: 'campaign_content',
        message: 'Email content is verplicht',
        action: 'Vul email content in voor je campaign',
        actionLink: `/campaigns/edit/${campaignId}`,
      });
    }
  }

  // NB: de afzender wordt server-side bepaald in send-scheduled-campaign-now /
  // send-test-email. Zonder geverifieerd eigen domein valt het systeem altijd
  // terug op noreply@lees-nu.nl met de bedrijfs-/voornaam als afzendernaam.
  // Daarom is hier GEEN afzender-/SendGrid-check nodig: die zou alleen een
  // valse blokkade opwerpen voor users zonder eigen domein (de meeste).
  const { data: user, error: userError } = await supabase
    .from('users')
    .select('address, city, postal_code')
    .eq('id', userId)
    .single();

  if (userError || !user) {
    errors.push({
      type: 'business_info',
      message: 'Kon gebruikersgegevens niet laden',
    });
    return { isValid: false, errors };
  }

  if (!user.address || !user.city || !user.postal_code) {
    errors.push({
      type: 'business_info',
      message: 'Bedrijfsgegevens incompleet',
      action: 'Vul je bedrijfsadres in voor de email footer',
      actionLink: '/dashboard/settings',
    });
  }

  const hasTagFilter = !!filterTags && filterTags.length > 0;
  let subscriberQuery = supabase
    .from('email_contacts')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('subscribed', true);

  if (hasTagFilter) {
    subscriberQuery = subscriberQuery.overlaps('tags', filterTags);
  }

  const { count: subscriberCount, error: subscriberError } = await subscriberQuery;

  if (subscriberError) {
    errors.push({
      type: 'subscribers',
      message: 'Kon subscribers niet laden',
    });
    return { isValid: false, errors };
  }

  if (!subscriberCount || subscriberCount === 0) {
    errors.push({
      type: 'subscribers',
      message: hasTagFilter ? 'Geen geabonneerde contacten in de gekozen groep(en)' : 'Geen geabonneerde contacten',
      action: hasTagFilter ? 'Kies een andere groep of voeg contacten toe aan deze groep' : 'Voeg eerst contacten toe aan je mailinglijst',
      actionLink: '/mailinglist',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    subscriberCount: subscriberCount || 0,
  };
}

export async function validateCampaignContent(campaignId: string): Promise<{ isValid: boolean; errors: string[] }> {
  const { data: campaign, error } = await supabase
    .from('email_campaigns')
    .select('subject, content')
    .eq('id', campaignId)
    .single();

  if (error || !campaign) {
    return { isValid: false, errors: ['Kon campaign niet laden'] };
  }

  const errors: string[] = [];

  if (!campaign.subject || !campaign.subject.trim()) {
    errors.push('Subject is verplicht');
  }

  if (!campaign.content || !campaign.content.trim()) {
    errors.push('Email content is verplicht');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// filterTags: doelgroep-groepen (OR-match); leeg = alle geabonneerde contacten.
export async function getSubscriberCount(userId: string, filterTags?: string[]): Promise<number> {
  let query = supabase
    .from('email_contacts')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('subscribed', true);

  if (filterTags && filterTags.length > 0) {
    query = query.overlaps('tags', filterTags);
  }

  const { count, error } = await query;

  if (error) {
    console.error('Error getting subscriber count:', error);
    return 0;
  }

  return count || 0;
}
